import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  Loader2,
  RefreshCcw,
  ShieldAlert,
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useOperationalContext } from '../../../contexts/OperationalContext';
import { supabase } from '../../../lib/supabaseClient';
import { PermissionKey } from '../../../types/workspace';
import { useAppToast } from '../../../components/AppToast';
import { useDrawerHost } from '../../../components/drawer/DrawerHostProvider';
import { GestaoTaskDrawer } from '../../gestao/components/GestaoTaskDrawer';
import { useGestaoMutations } from '../../gestao/hooks/useGestaoMutations';
import { MinhaMesaCompletionBar } from '../../minha-mesa/components/MinhaMesaCompletionBar';
import { MinhaMesaTaskDetail } from '../../minha-mesa/components/MinhaMesaTaskDetail';
import type { MinhaMesaTab } from '../../minha-mesa/types';
import { useMyTaskDetail } from '../../minha-mesa/hooks/useMyTaskDetail';
import { useMyTaskMutations } from '../../minha-mesa/hooks/useMyTaskMutations';
import { ExecutionStatusBadge } from '../components/ExecutionStatusBadge';
import { useExecutionDetails } from '../hooks/useExecutionDetails';
import { useExecutionMutations } from '../hooks/useExecutionMutations';
import { useExecutionRealtime } from '../hooks/useExecutionRealtime';
import { useInstanceAudit } from '../hooks/useInstanceAudit';
import { useInstanceEvents } from '../hooks/useInstanceEvents';
import { useInstanceStages } from '../hooks/useInstanceStages';
import { useExecutionModuleContext } from '../context/ExecutionModuleContext';
import { getOperationalRiskLevel } from '../../shared/utils/operationalRisk';

type SnapshotStage = {
  order?: number | null;
  slaHours?: number | null;
  tp_standard?: number | null;
  tra_standard?: number | null;
  expected_error_percent?: number | null;
  correction_time_standard?: number | null;
  warning_threshold_percent?: number | null;
  metadata?: Record<string, unknown> | null;
};

type SnapshotMetrics = {
  tpStandardMinutes: number | null;
  traStandardMinutes: number | null;
  errorStandardPercent: number | null;
  correctionTimeStandardMinutes: number | null;
  warningThresholdPercent: number;
};

function diffMs(start?: string | null, end?: string | null) {
  if (!start || !end) return null;
  const diff = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(diff)) return null;
  return diff;
}

function formatDurationClock(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/D';
  const sign = value < 0 ? '-' : '';
  const totalSeconds = Math.max(Math.round(Math.abs(value) / 1000), 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatDurationClockFromMinutes(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/D';
  return formatDurationClock(value * 60 * 1000);
}

function formatPercent(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/D';
  return `${value.toFixed(1)}%`;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function extractSnapshotMetrics(snapshot: Record<string, any> | null): SnapshotMetrics {
  if (!snapshot) {
    return {
      tpStandardMinutes: null,
      traStandardMinutes: null,
      errorStandardPercent: null,
      correctionTimeStandardMinutes: null,
      warningThresholdPercent: 80,
    };
  }

  const stages = Array.isArray(snapshot.stages) ? (snapshot.stages as SnapshotStage[]) : [];
  const tpFromStages = stages.reduce((acc, stage) => {
    const tpStandard = toNumber(stage.tp_standard);
    if (tpStandard !== null) return acc + tpStandard;
    const slaHours = toNumber(stage.slaHours);
    return slaHours !== null ? acc + slaHours * 60 : acc;
  }, 0);
  const traFromStages = stages.reduce((acc, stage) => {
    const value = toNumber(stage.tra_standard);
    return value !== null ? acc + value : acc;
  }, 0);
  const correctionFromStages = stages.reduce((acc, stage) => {
    const value = toNumber(stage.correction_time_standard);
    return value !== null ? acc + value : acc;
  }, 0);
  const stageThresholds = stages
    .map((stage) => toNumber(stage.warning_threshold_percent))
    .filter((value): value is number => value !== null);

  const rootTpMetrics = [snapshot.tp_standard, snapshot.metrics?.tp_standard];
  const rootTraMetrics = [
    snapshot.tra_standard,
    snapshot.tra,
    snapshot.tempoRetrabalhoPadrao,
    snapshot.metrics?.tra_standard,
    snapshot.metrics?.tra,
    snapshot.metrics?.tempoRetrabalhoPadrao,
  ];
  const errorMetrics = [
    snapshot.expected_error_percent,
    snapshot.errorPercent,
    snapshot.percentualErro,
    snapshot.metrics?.expected_error_percent,
    snapshot.metrics?.errorPercent,
    snapshot.metrics?.percentualErro,
  ];
  const correctionMetrics = [snapshot.correction_time_standard, snapshot.metrics?.correction_time_standard];
  const warningThresholdMetrics = [
    snapshot.warning_threshold_percent,
    snapshot.metrics?.warning_threshold_percent,
    ...stageThresholds,
  ];

  return {
    tpStandardMinutes: rootTpMetrics.map(toNumber).find((value) => value !== null) ?? (tpFromStages > 0 ? tpFromStages : null),
    traStandardMinutes: rootTraMetrics.map(toNumber).find((value) => value !== null) ?? (traFromStages > 0 ? traFromStages : null),
    errorStandardPercent: errorMetrics.map(toNumber).find((value) => value !== null) ?? null,
    correctionTimeStandardMinutes:
      correctionMetrics.map(toNumber).find((value) => value !== null) ?? (correctionFromStages > 0 ? correctionFromStages : null),
    warningThresholdPercent: warningThresholdMetrics.map(toNumber).find((value) => value !== null) ?? 80,
  };
}

function getRiskTone(params: {
  isOverdue: boolean;
  reprocessCount: number;
  tpDeviationPercent: number | null;
  warningThresholdPercent: number;
}) {
  if (params.isOverdue || params.reprocessCount > 0) {
    return {
      label: 'Critico',
      className: 'border-rose-300/70 bg-rose-50/70 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-300',
    };
  }

  if (params.tpDeviationPercent !== null && params.tpDeviationPercent > params.warningThresholdPercent) {
    return {
      label: 'Alerta',
      className: 'border-amber-300/70 bg-amber-50/70 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-300',
    };
  }

  return {
    label: 'Controlado',
    className: 'border-sky-300/70 bg-sky-50/70 text-sky-800 dark:border-sky-900/60 dark:bg-sky-950/20 dark:text-sky-300',
  };
}

function getDeviationTone(params: {
  deviationPercent: number | null;
  threshold: number;
}) {
  if (params.deviationPercent === null) {
      return {
        cardClassName: '',
        valueClassName: '',
        badgeClassName: 'border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
        label: 'N/D',
      };
    }

  if (params.deviationPercent > params.threshold * 2) {
    return {
      cardClassName: 'border-rose-200/80 bg-rose-50/60 dark:border-rose-900/50 dark:bg-rose-950/20',
      valueClassName: 'text-rose-700 dark:text-rose-300',
      badgeClassName: 'border-rose-300/70 bg-rose-50/70 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-300',
      label: 'Critico',
    };
  }

  if (params.deviationPercent > params.threshold) {
    return {
      cardClassName: 'border-amber-200/80 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20',
      valueClassName: 'text-amber-700 dark:text-amber-300',
      badgeClassName: 'border-amber-300/70 bg-amber-50/70 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-300',
      label: 'Alerta',
    };
  }

  return {
    cardClassName: 'border-sky-200/80 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-950/20',
    valueClassName: 'text-sky-800 dark:text-sky-300',
    badgeClassName: 'border-sky-300/70 bg-sky-50/70 text-sky-800 dark:border-sky-900/60 dark:bg-sky-950/20 dark:text-sky-300',
    label: 'Controlado',
  };
}

export const ExecutionInstancePage: React.FC = () => {
  const navigate = useNavigate();
  const { id: executionId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { openDrawer } = useDrawerHost();
  const { setRibbonState, clearRibbonState } = useExecutionModuleContext();
  const { showToast, ToastContainer } = useAppToast();
  const { setActiveExecution, setActiveStage } = useOperationalContext();
  const organizationId = workspace?.organizationId ?? user?.organization_id ?? user?.organizationId;
  const userId = user?.id;
  const userName = user?.name;
  const canPause = workspace?.permissions[PermissionKey.CAN_PAUSE_EXECUTION] ?? false;
  const canCancel = workspace?.permissions[PermissionKey.CAN_CANCEL_EXECUTION] ?? false;
  const canReprocess = workspace?.permissions[PermissionKey.CAN_REPROCESS_EXECUTION] ?? false;
  const canViewBlueprintPerformance = workspace?.permissions[PermissionKey.CAN_VIEW_BLUEPRINT_PERFORMANCE] ?? false;
  const defaultWarningThresholdPercent = 80;

  const [activeTab, setActiveTab] = useState<MinhaMesaTab>('instrucoes');
  const [formState, setFormState] = useState<Record<string, unknown>>({});
  const [checklistState, setChecklistState] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [isReprocessOpen, setIsReprocessOpen] = useState(searchParams.get('action') === 'reprocess');
  const [reprocessReason, setReprocessReason] = useState('');
  const [focusedPanel, setFocusedPanel] = useState<'history' | 'timeline' | 'technical' | 'metrics' | null>(null);
  const [clockTick, setClockTick] = useState(() => Date.now());

  const metricsSectionRef = useRef<HTMLElement | null>(null);
  const historySectionRef = useRef<HTMLElement | null>(null);
  const timelineSectionRef = useRef<HTMLElement | null>(null);
  const technicalSectionRef = useRef<HTMLElement | null>(null);

  const detailsQuery = useExecutionDetails(organizationId, executionId);
  const { stages, isLoading: stagesLoading } = useInstanceStages(organizationId, executionId);
  const { events, isLoading: eventsLoading } = useInstanceEvents(organizationId, executionId);
  const { audit } = useInstanceAudit(organizationId, executionId);
  const executionMutations = useExecutionMutations({ organizationId });
  const gestaoMutations = useGestaoMutations();

  useExecutionRealtime(executionId, organizationId, Boolean(executionId && organizationId));

  const snapshotQuery = useQuery({
    queryKey: ['execution', organizationId ?? 'unknown', executionId ?? 'unknown', 'snapshot', audit?.blueprintVersionId ?? 'none'],
    enabled: Boolean(organizationId && audit?.blueprintVersionId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('process_blueprint_versions')
        .select('snapshot_json')
        .eq('id', audit!.blueprintVersionId!)
        .maybeSingle();

      if (error) throw error;
      return (data?.snapshot_json as Record<string, any> | null) ?? null;
    },
    staleTime: 60_000,
  });

  const execution = detailsQuery.data?.instance ?? null;
  const currentStage = useMemo(() => {
    if (!execution) {
      return stages.find((stage) => stage.status === 'in_progress') ?? stages.find((stage) => stage.status === 'pending') ?? null;
    }

    return (
      stages.find((stage) => stage.id === execution.currentStageId) ??
      stages.find((stage) => stage.status === 'in_progress') ??
      stages.find((stage) => stage.status === 'pending') ??
      null
    );
  }, [execution, stages]);

  const currentTaskId = currentStage?.taskId ?? detailsQuery.data?.tasks.find((task) => {
    const status = String(task.status);
    return status === 'pending' || status === 'in_progress' || status === 'waiting';
  })?.id ?? null;

  const { task, isLoading: taskLoading, logs, logsLoading, refetch: refetchTask } = useMyTaskDetail(currentTaskId);

  const handleTaskMutationSuccess = useCallback(async () => {
    await Promise.all([detailsQuery.refetch(), refetchTask()]);
  }, [detailsQuery.refetch, refetchTask]);

  const taskMutations = useMyTaskMutations({
    organizationId,
    userId,
    userName,
    onSuccess: handleTaskMutationSuccess,
  });

  useEffect(() => {
    const timer = window.setInterval(() => setClockTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!execution) return;
    setActiveExecution(
      execution.id,
      execution.referenceCode ?? execution.title ?? execution.id,
      execution.templateId,
      execution.templateName,
    );
    setActiveStage(currentStage?.id ?? execution.currentStageId ?? null, currentStage?.name ?? execution.currentStageName ?? null);
  }, [currentStage?.id, currentStage?.name, execution, setActiveExecution, setActiveStage]);

  useEffect(() => {
    if (!task) return;
    setFormState(task.formData && Object.keys(task.formData).length > 0 ? task.formData : {});
    setChecklistState(task.checklistData && Object.keys(task.checklistData).length > 0 ? task.checklistData : {});
    setNotes(task.notes ?? '');
  }, [task]);

  const snapshotMetrics = extractSnapshotMetrics(snapshotQuery.data ?? null);
  const tpRealMinutes = useMemo(() => {
    const values = stages
      .map((stage) => stage.leadTimeMinutes)
      .filter((value): value is number => value !== null && value !== undefined);
    if (values.length === 0) return null;
    return values.reduce((sum, value) => sum + value, 0);
  }, [stages]);
  const traRealMinutes = useMemo(() => {
    const values = stages
      .map((stage) => stage.processingMinutes)
      .filter((value): value is number => value !== null && value !== undefined);
    if (values.length === 0) return null;
    return values.reduce((sum, value) => sum + value, 0);
  }, [stages]);
  const warningThresholdPercent =
    currentStage?.warningThresholdPercent ??
    snapshotMetrics.warningThresholdPercent ??
    defaultWarningThresholdPercent;
  const tpDeviationPercent = snapshotMetrics.tpStandardMinutes && tpRealMinutes !== null
    ? ((tpRealMinutes - snapshotMetrics.tpStandardMinutes) / snapshotMetrics.tpStandardMinutes) * 100
    : null;
  const riskTone = getRiskTone({
    isOverdue: execution?.isOverdue ?? false,
    reprocessCount: execution?.reprocessCount ?? 0,
    tpDeviationPercent,
    warningThresholdPercent,
  });
  const stageReprocessCounts = useMemo(() => {
    const counts = new Map<string, number>();
    events
      .filter((event) => event.eventType === 'task_reprocessed' && event.stageId)
      .forEach((event) => counts.set(event.stageId!, (counts.get(event.stageId!) ?? 0) + 1));
    return counts;
  }, [events]);
  const stageSlaMinutes = currentStage?.slaHours ? currentStage.slaHours * 60 : null;
  const currentStageTpRealMinutes = currentStage?.leadTimeMinutes ?? null;
  const stageDeviationPercent = stageSlaMinutes && currentStageTpRealMinutes !== null
    ? ((currentStageTpRealMinutes - stageSlaMinutes) / stageSlaMinutes) * 100
    : null;
  const structuralAlert =
    Boolean(execution?.isOverdue) ||
    (execution?.reprocessCount ?? 0) > 0 ||
    (tpDeviationPercent !== null && tpDeviationPercent > warningThresholdPercent);

  const isTaskStarted = ['in_progress', 'completed', 'failed'].includes(String(task?.status));
  const canComplete = useMemo(() => {
    if (!task) return false;
    const requiredFields = task.formFields.filter((field) => field.required);
    return requiredFields.every((field) => {
      const value = formState[field.id];
      return value !== undefined && value !== null && value !== '';
    });
  }, [formState, task]);

  const handleStartTask = useCallback(async () => {
    if (!task) return;
    if (String(task.status) === 'pending') {
      await taskMutations.startTask(task.id);
    }
  }, [task, taskMutations]);

  const handleCompleteTask = useCallback(async (result: 'completed' | 'approved' = 'completed') => {
    if (!task || !userId) return;
    if (String(task.status) === 'pending') {
      await taskMutations.startTask(task.id);
    }

    await taskMutations.completeTask({
      taskId: task.id,
      instanceId: task.instanceId,
      result,
      notes,
      formData: formState,
      checklistData: checklistState,
      completedBy: userId,
      previousUpdatedAt: task.updatedAt,
      blueprintVersionId: task.metadata?.blueprint_version_id as string | undefined,
    });
  }, [checklistState, formState, notes, task, taskMutations, userId]);

  const handleRejectTask = useCallback(async (reason: string) => {
    if (!task) return;
    await taskMutations.rejectTask({ taskId: task.id, reason, instanceId: task.instanceId });
  }, [task, taskMutations]);

  const handleBlockTask = useCallback(async () => {
    if (!task) return;
    await taskMutations.rejectTask({
      taskId: task.id,
      instanceId: task.instanceId,
      reason: 'Bloqueado na tela dedicada da instancia para analise operacional',
    });
  }, [task, taskMutations]);

  const handleSaveTask = useCallback(async () => {
    if (!task) return;
    await taskMutations.saveForm({
      taskId: task.id,
      formData: formState,
      checklistData: checklistState,
    });
    showToast('success', 'Rascunho salvo na task atual.');
  }, [checklistState, formState, showToast, task, taskMutations]);

  const handleExecutionAction = useCallback(async (action: 'pause' | 'resume' | 'cancel') => {
    if (!execution) return;

    try {
      if (action === 'pause') {
        await executionMutations.pauseExecution.mutate(execution.id);
        showToast('info', 'Execucao pausada.');
      }
      if (action === 'resume') {
        await executionMutations.resumeExecution.mutate(execution.id);
        showToast('success', 'Execucao retomada.');
      }
      if (action === 'cancel') {
        await executionMutations.cancelExecution.mutate({ executionId: execution.id });
        showToast('warning', 'Execucao cancelada.');
      }
      await detailsQuery.refetch();
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao executar acao.');
    }
  }, [detailsQuery.refetch, execution, executionMutations.cancelExecution, executionMutations.pauseExecution, executionMutations.resumeExecution, showToast]);

  const handleReprocess = useCallback(async () => {
    if (!execution || !canReprocess) return;

    try {
      const result = await executionMutations.reprocessExecution.mutate({
        executionId: execution.id,
        reason: reprocessReason.trim() || undefined,
      });
      showToast('success', 'Nova instancia criada a partir deste historico.');
      setIsReprocessOpen(false);
      setReprocessReason('');
      if (result?.id) {
        navigate(`/execucoes/instancias/${encodeURIComponent(result.id)}`);
      }
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Falha ao reprocessar execucao.');
    }
  }, [canReprocess, execution, executionMutations.reprocessExecution, navigate, reprocessReason, showToast]);

  const handleCopyValue = useCallback(async (label: string, value?: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      showToast('success', `${label} copiado.`);
    } catch {
      showToast('error', `Falha ao copiar ${label.toLowerCase()}.`);
    }
  }, [showToast]);

  const focusOperationalSection = useCallback((section: 'history' | 'timeline' | 'technical' | 'metrics') => {
    const refMap = {
      history: historySectionRef,
      timeline: timelineSectionRef,
      technical: technicalSectionRef,
      metrics: metricsSectionRef,
    };

    setFocusedPanel(section);
    refMap[section].current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  useEffect(() => {
    if (!focusedPanel) return;
    const timeoutId = window.setTimeout(() => setFocusedPanel(null), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [focusedPanel]);

  const actionBusy =
    executionMutations.pauseExecution.isLoading ||
    executionMutations.resumeExecution.isLoading ||
    executionMutations.cancelExecution.isLoading ||
    executionMutations.reprocessExecution.isLoading;
  const now = clockTick;
  const currentStageElapsedMs = currentStage
    ? currentStage.completedAt
      ? diffMs(currentStage.enteredAt, currentStage.completedAt)
      : currentStage.enteredAt
        ? Math.max(now - new Date(currentStage.enteredAt).getTime(), 0)
        : currentStageTpRealMinutes !== null
          ? currentStageTpRealMinutes * 60 * 1000
          : null
    : null;
  const currentStageRemainingMs = currentStage?.slaDeadline
    ? new Date(currentStage.slaDeadline).getTime() - now
    : stageSlaMinutes !== null && currentStageElapsedMs !== null
      ? (stageSlaMinutes * 60 * 1000) - currentStageElapsedMs
      : null;
  const stageSlaLabel = stageSlaMinutes ? formatDurationClockFromMinutes(stageSlaMinutes) : 'N/D';
  const stageRemainingLabel = formatDurationClock(currentStageRemainingMs);
  const currentStageElapsedLabel = formatDurationClock(currentStageElapsedMs);
  const tpStandardLabel = formatDurationClockFromMinutes(snapshotMetrics.tpStandardMinutes);
  const tpRealLabel = formatDurationClockFromMinutes(tpRealMinutes);
  const traStandardLabel = formatDurationClockFromMinutes(snapshotMetrics.traStandardMinutes);
  const traRealLabel = formatDurationClockFromMinutes(traRealMinutes);
  const tpDeviationTone = getDeviationTone({ deviationPercent: tpDeviationPercent, threshold: warningThresholdPercent });
  const reprocessTone = (execution?.reprocessCount ?? 0) > 0
    ? {
        cardClassName: 'border-amber-200/80 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20',
        valueClassName: 'text-rose-700 dark:text-rose-300',
      }
    : {
        cardClassName: 'border-slate-200/80 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-900/60',
        valueClassName: 'text-slate-900 dark:text-slate-100',
      };
  const snapshotStages = Array.isArray(snapshotQuery.data?.stages)
    ? (snapshotQuery.data?.stages as SnapshotStage[])
    : [];
  const workflowRows = (() => {
    let accumulatedRealMinutes = 0;

    return stages.map((stage) => {
      const snapshotStage = snapshotStages.find((candidate, index) => {
        const order = toNumber(candidate.order) ?? index + 1;
        return order === stage.stageOrder;
      });
      const tpStandardMinutes = toNumber(snapshotStage?.tp_standard) ?? (() => {
        const slaHours = toNumber(snapshotStage?.slaHours);
        return slaHours !== null ? slaHours * 60 : null;
      })();
      const stageRealMinutes = stage.leadTimeMinutes ?? null;
      if (stageRealMinutes !== null) {
        accumulatedRealMinutes += stageRealMinutes;
      }

      return {
        ...stage,
        tpStandardMinutes,
        accumulatedRealMinutes: accumulatedRealMinutes > 0 ? accumulatedRealMinutes : null,
        reprocessCount: stageReprocessCounts.get(stage.id) ?? 0,
      };
    });
  })();
  const stageWarnings = stages.filter((stage) => ['warning', 'overdue', 'completed_late'].includes(stage.slaStatus));
  const recentEvents = events.slice(0, 5);
  const recentLogs = logs.slice(0, 5);
  const hasAttachmentRequirement = Boolean(task?.exitOptions?.requireAttachment) || Boolean(task?.formFields.some((field) => field.type === 'file'));
  const canStartCurrentTask = Boolean(task && String(task.status) === 'pending');
  const canPauseOrResume = Boolean(execution && canPause && (execution.status === 'active' || execution.status === 'on_hold'));
  const canCancelCurrentExecution = Boolean(execution && canCancel && execution.status !== 'completed' && execution.status !== 'cancelled');
  const canOpenBlueprint = Boolean(audit?.blueprintId);
  const currentRoleLabel = String(task?.assignedRoleName ?? task?.metadata?.roleName ?? task?.metadata?.role ?? 'N/D').replace(/\s+/g, '_').toUpperCase();
  const currentExecutorLabel = String(task?.assignedUserName ?? task?.assignedToName ?? 'NAO_ASSUMIDO').replace(/\s+/g, '_').toUpperCase();
  const stageRiskLevel = getOperationalRiskLevel(
    currentStageRemainingMs !== null ? currentStageRemainingMs / 60000 : null,
    stageSlaMinutes,
  );
  const stageRiskBarClass = stageRiskLevel === 'CRITICO'
    ? 'border-l-rose-500 dark:border-l-rose-400'
    : stageRiskLevel === 'ALERTA'
      ? 'border-l-amber-500 dark:border-l-amber-400'
      : 'border-l-emerald-500 dark:border-l-emerald-400';
  const breachMetricLabel = currentStageRemainingMs !== null && currentStageRemainingMs < 0 ? 'EM_RUPTURA' : 'BREACH_EM';
  const breachMetricValue = currentStageRemainingMs === null ? 'N/D' : formatDurationClock(Math.abs(currentStageRemainingMs));
  const breachMetricClassName = currentStageRemainingMs === null
    ? 'text-slate-900 dark:text-slate-100'
    : currentStageRemainingMs < 0
      ? 'text-rose-700 dark:text-rose-300'
      : stageSlaMinutes !== null && currentStageRemainingMs <= stageSlaMinutes * 60 * 1000 * 0.15
        ? 'text-amber-700 dark:text-amber-300'
        : 'text-slate-900 dark:text-slate-100';
  const governanceRows = [
    { label: 'INSTANCIA_ID', value: execution?.id ?? null },
    { label: 'BLUEPRINT_ID', value: audit?.blueprintId ?? null },
    { label: 'SNAPSHOT_HASH', value: audit?.snapshotHash ?? null },
    { label: 'DERIVADA_DE', value: execution?.reprocessedFromId ?? null },
  ].filter((row) => row.value);
  const openCreateExecution = useCallback(() => {
    navigate('/execucoes/instancias?create=1');
  }, [navigate]);
  const handleOpenBlueprintOverview = useCallback(() => {
    if (!audit?.blueprintId) return;
    openDrawer({ module: 'excelencia', entityId: audit.blueprintId, mode: 'overview' });
  }, [audit?.blueprintId, openDrawer]);
  const handleOpenBlueprintVersions = useCallback(() => {
    if (!audit?.blueprintId) return;
    openDrawer({ module: 'excelencia', entityId: audit.blueprintId, mode: 'versions' });
  }, [audit?.blueprintId, openDrawer]);
  const handleOpenAttachPanel = useCallback(() => {
    setActiveTab('formulario');
    showToast('info', 'Anexos desta etapa sao tratados dentro do formulario dinamico.');
  }, [showToast]);
  const handleOpenActionPanel = useCallback(() => {
    setShowActionPanel(true);
  }, []);
  const handleOpenReprocessPanel = useCallback(() => {
    setIsReprocessOpen(true);
  }, []);
  const ribbonState = useMemo(() => {
    if (!execution) {
      return null;
    }

    return {
      create: {
        enabled: true,
        onClick: openCreateExecution,
      },
      instance: {
        pauseLabel: execution.status === 'on_hold' ? 'Retomar' : 'Pausar',
        onPauseOrResume: () => void handleExecutionAction(execution.status === 'on_hold' ? 'resume' : 'pause'),
        canPauseOrResume,
        onCancel: () => void handleExecutionAction('cancel'),
        canCancel: canCancelCurrentExecution,
        onReprocess: handleOpenReprocessPanel,
        canReprocess,
        busy: actionBusy,
      },
      stage: {
        onStart: () => void handleStartTask(),
        canStart: canStartCurrentTask && !taskMutations.isStarting,
        onComplete: () => void handleCompleteTask(task?.completionType === 'approve_reject' ? 'approved' : 'completed'),
        canComplete: Boolean(task && canComplete && !taskMutations.isCompleting),
        onBlock: undefined,
        canBlock: false,
        onOpenAction: handleOpenActionPanel,
        canOpenAction: Boolean(task),
      },
      engineering: {
        onViewBlueprint: handleOpenBlueprintOverview,
        canViewBlueprint: canOpenBlueprint,
        onCompareVersion: handleOpenBlueprintVersions,
        canCompareVersion: canViewBlueprintPerformance && canOpenBlueprint,
        onStandardVsReal: () => focusOperationalSection('metrics'),
        canStandardVsReal: true,
      },
    };
  }, [
    actionBusy,
    canCancelCurrentExecution,
    canComplete,
    canOpenBlueprint,
    canPauseOrResume,
    canReprocess,
    canStartCurrentTask,
    canViewBlueprintPerformance,
    execution,
    focusOperationalSection,
    handleCompleteTask,
    handleExecutionAction,
    handleOpenActionPanel,
    handleOpenBlueprintOverview,
    handleOpenBlueprintVersions,
    handleOpenReprocessPanel,
    handleStartTask,
    openCreateExecution,
    task,
    taskMutations.isCompleting,
    taskMutations.isStarting,
  ]);

  useEffect(() => {
    setRibbonState(ribbonState);
  }, [ribbonState, setRibbonState]);

  useEffect(() => () => clearRibbonState(), [clearRibbonState]);

  if (!organizationId || !executionId) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50 text-sm text-slate-500 dark:bg-[var(--bg-workspace)] dark:text-slate-400">
        Instancia nao encontrada.
      </div>
    );
  }

  if (detailsQuery.isLoading || !execution) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50 dark:bg-[var(--bg-workspace)]">
        <Loader2 className="animate-spin text-slate-500" size={28} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-transparent">
      <main className="flex-1 overflow-hidden px-4 pb-3 pt-1 sm:px-6">
        <div className="flex h-full min-h-0 flex-col gap-2.5">
          <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] font-normal uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
            <span className="text-slate-800 dark:text-slate-200">{execution.referenceCode ?? execution.id}</span>
            <span>/</span>
            <span>{audit?.blueprintName ?? execution.templateName ?? 'Modelo não identificado'}</span>
            {audit?.versionSemver ? (
              <>
                <span>/</span>
                <span>{audit.versionSemver}</span>
              </>
              ) : null}
            <span>/</span>
            <ExecutionStatusBadge status={execution.status} />
            <span className={`inline-flex items-center rounded-[4px] border px-1.5 py-0.5 ${riskTone.className}`}>{riskTone.label.toUpperCase()}</span>
          </div>

          <section className="bg-transparent">
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <h2 className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Workflow</h2>
              {stagesLoading || snapshotQuery.isFetching ? <Loader2 size={16} className="animate-spin text-slate-400" /> : null}
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1 pt-0.5">
              {workflowRows.map((stage, index) => {
                const isCurrent = currentStage?.id === stage.id;
                const stageTone =
                  stage.status === 'completed'
                    ? 'border-slate-300/80 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/70'
                    : isCurrent
                      ? 'border-slate-500 bg-slate-100/90 dark:border-slate-500 dark:bg-slate-900'
                      : 'border-slate-200/80 bg-white/60 dark:border-slate-800 dark:bg-slate-900/40';

                return (
                  <React.Fragment key={stage.id}>
                    <div className={`min-h-[112px] min-w-[210px] rounded-[6px] border px-3 py-2.5 ${stageTone}`}>
                      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                        {stage.status === 'completed' ? 'CONCLUIDA' : isCurrent ? 'ATUAL' : 'FUTURA'}
                      </div>
                      <p className={`mt-1 truncate text-[12px] font-medium uppercase tracking-[0.12em] ${isCurrent ? 'text-slate-950 dark:text-white' : 'text-slate-700 dark:text-slate-200'}`}>
                        {stage.name.replace(/\s+/g, '_')}
                      </p>
                      <div className="mt-3 space-y-1 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                        <div className="flex items-center justify-between gap-2">
                          <span>TP_STD</span>
                          <span className="text-slate-800 dark:text-slate-100">{formatDurationClockFromMinutes(stage.tpStandardMinutes)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span>TP_REAL</span>
                          <span className="text-slate-800 dark:text-slate-100">{formatDurationClockFromMinutes(stage.accumulatedRealMinutes)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span>REPROC</span>
                          <span className="text-slate-800 dark:text-slate-100">{stage.reprocessCount}</span>
                        </div>
                      </div>
                    </div>
                    {index < workflowRows.length - 1 ? (
                      <div className="flex w-6 items-center justify-center">
                        <div className="h-px w-full bg-slate-300 dark:bg-slate-700" />
                      </div>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </div>
          </section>

          {isReprocessOpen ? (
            <section className="rounded-[6px] border border-slate-200 p-4 dark:border-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Reprocessar instancia</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Descreva o motivo para preservar a rastreabilidade desta nova rodada.</p>
                </div>
                <button type="button" onClick={() => setIsReprocessOpen(false)} className="rounded-[4px] px-2 py-1 text-xs font-semibold text-slate-500">
                  Fechar
                </button>
              </div>
              <textarea
                value={reprocessReason}
                onChange={(event) => setReprocessReason(event.target.value)}
                rows={3}
                className="mt-3 w-full rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => void handleReprocess()}
                  disabled={executionMutations.reprocessExecution.isLoading}
                  className="inline-flex items-center gap-2 rounded-[4px] bg-slate-950 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-slate-900"
                >
                  {executionMutations.reprocessExecution.isLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
                  Confirmar reprocesso
                </button>
              </div>
            </section>
          ) : null}

          {structuralAlert ? (
            <section className="rounded-[6px] border border-amber-200 bg-amber-50/70 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <ShieldAlert size={18} className="mt-0.5 text-amber-600 dark:text-amber-300" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Desvio estrutural detectado. Deseja abrir acao vinculada?</p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      O alerta considera atraso, reprocesso ou desvio de TP acima de {warningThresholdPercent}%.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowActionPanel(true)}
                  className="rounded-[4px] border border-amber-600/30 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:bg-slate-900 dark:text-amber-200"
                >
                  Abrir acao
                </button>
              </div>
            </section>
          ) : null}

          <section className="grid min-h-0 flex-1 gap-3 xl:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]">
            <div className="flex min-h-0 flex-col overflow-hidden">
              <div className="border border-slate-200/90 p-4 dark:border-slate-800">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className={`min-w-0 flex-1 border-l-[3px] pl-4 ${stageRiskBarClass}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">ETAPA_ATUAL</p>
                    <h2 className="mt-1 text-[1.65rem] font-semibold uppercase tracking-[0.04em] text-slate-950 dark:text-white">
                      {(currentStage?.name ?? execution.currentStageName ?? 'ETAPA_N_D').replace(/\s+/g, '_')}
                    </h2>
                    <div className="mt-4 max-w-[520px] space-y-1.5 font-mono text-[12px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                      <div className="flex items-center justify-between gap-4 border-t border-slate-200/80 pt-2 dark:border-slate-800">
                        <span>PAPEL</span>
                        <span className="text-right text-slate-900 dark:text-slate-100">{currentRoleLabel}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 border-t border-slate-200/80 pt-2 dark:border-slate-800">
                        <span>EXECUTOR</span>
                        <span className="text-right text-slate-900 dark:text-slate-100">{currentExecutorLabel}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 border-t border-slate-200/80 pt-2 dark:border-slate-800">
                        <span>SLA_REST</span>
                        <span className={`text-right ${currentStageRemainingMs !== null && currentStageRemainingMs < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-slate-900 dark:text-slate-100'}`}>{stageRemainingLabel}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 border-t border-slate-200/80 pt-2 dark:border-slate-800">
                        <span>{breachMetricLabel}</span>
                        <span className={`text-right ${breachMetricClassName}`}>{breachMetricValue}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 border-t border-slate-200/80 pt-2 dark:border-slate-800">
                        <span>DECORRIDO</span>
                        <span className="text-right text-slate-900 dark:text-slate-100">{currentStageElapsedLabel}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 border-t border-slate-200/80 pt-2 dark:border-slate-800">
                        <span>REPROC</span>
                        <span className="text-right text-slate-900 dark:text-slate-100">{execution.reprocessCount}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 border-t border-slate-200/80 pt-2 dark:border-slate-800">
                        <span>ERRO_%</span>
                        <span className="text-right text-slate-900 dark:text-slate-100">{formatPercent(snapshotMetrics.errorStandardPercent)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-auto flex flex-wrap justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => void handleCompleteTask(task?.completionType === 'approve_reject' ? 'approved' : 'completed')}
                      disabled={!task || !canComplete || taskMutations.isCompleting}
                      className="inline-flex items-center justify-center gap-2 rounded-[4px] bg-slate-950 px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                    >
                      <CheckCircle2 size={14} />
                      CONCLUIR_ETAPA
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleStartTask()}
                      disabled={!task || String(task.status) !== 'pending' || taskMutations.isStarting}
                      className="rounded-[4px] border border-slate-300 bg-transparent px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                    >
                      INICIAR
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleBlockTask()}
                      disabled={!task || taskMutations.isRejecting}
                      className="rounded-[4px] border border-amber-300/80 bg-transparent px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-amber-800 hover:bg-amber-50 disabled:opacity-50 dark:border-amber-900/60 dark:text-amber-200 dark:hover:bg-amber-950/20"
                    >
                      BLOQUEAR
                    </button>
                  </div>
                </div>

                {hasAttachmentRequirement ? (
                  <div className="mt-3 border-l-2 border-slate-300 pl-3 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300">
                    Os anexos exigidos desta etapa sao tratados dentro do formulario dinamico e preservados na resposta estruturada.
                  </div>
                ) : null}
              </div>

              {task ? (
                <>
                  <div className="mt-3 min-h-0 flex-1 overflow-hidden rounded-[6px] border border-slate-200/80 bg-white dark:border-slate-800 dark:bg-[var(--bg-surface)]">
                    <MinhaMesaTaskDetail
                      task={task}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      formState={formState}
                      checklistState={checklistState}
                      onFormFieldChange={(fieldId, value) => setFormState((prev) => ({ ...prev, [fieldId]: value }))}
                      onChecklistToggle={(itemId) => setChecklistState((prev) => ({ ...prev, [itemId]: !prev[itemId] }))}
                      logs={logs}
                      logsLoading={logsLoading}
                      isTaskStarted={isTaskStarted}
                      visibleTabs={['instrucoes', 'formulario', 'checklist']}
                      showHeader={false}
                      dense
                    />
                  </div>

                  <MinhaMesaCompletionBar
                    completionType={task.completionType}
                    exitOptions={task.exitOptions ?? {}}
                    checklist={task.checklist}
                    checklistState={checklistState}
                    notes={notes}
                    onNotesChange={setNotes}
                    onComplete={() => void handleCompleteTask('completed')}
                    onApprove={() => void handleCompleteTask('approved')}
                    onReject={(reason) => void handleRejectTask(reason)}
                    isCompleting={taskMutations.isCompleting}
                    isDisabled={taskLoading}
                    canComplete={canComplete}
                    dense
                  />
                </>
              ) : (
                <div className="flex min-h-[320px] flex-1 items-center justify-center px-6 text-sm text-slate-500 dark:text-slate-400">
                  Nenhuma task ativa vinculada a etapa atual.
                </div>
              )}
            </div>

            <aside className="flex h-full min-h-0 flex-col gap-2 border-l border-slate-200/80 pl-3 dark:border-slate-800">
              <section
                ref={metricsSectionRef}
                className={`rounded-[6px] border px-3 py-2.5 ${
                  focusedPanel === 'metrics'
                    ? 'border-slate-400 bg-slate-100/70 dark:border-slate-500 dark:bg-slate-900'
                    : 'border-slate-200/80 bg-slate-50/40 dark:border-slate-800 dark:bg-slate-900/50'
                }`}
              >
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Painel tecnico</p>
                <div className="mt-3 space-y-1.5 font-mono text-[12px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                  <div className="flex items-center justify-between gap-3">
                    <p>SLA_REST</p>
                    <p className={currentStageRemainingMs !== null && currentStageRemainingMs < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-slate-900 dark:text-slate-100'}>{stageRemainingLabel}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>{breachMetricLabel}</p>
                    <p className={breachMetricClassName}>{breachMetricValue}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>SLA_STD</p>
                    <p className="text-slate-900 dark:text-slate-100">{stageSlaLabel}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>DECORRIDO</p>
                    <p className="text-slate-900 dark:text-slate-100">{currentStageElapsedLabel}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>TP_STD</p>
                    <p className="text-slate-900 dark:text-slate-100">{tpStandardLabel}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>TP_REAL</p>
                    <p className={`${tpDeviationTone.valueClassName || 'text-slate-900 dark:text-slate-100'}`}>{tpRealLabel}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>TRA_STD</p>
                    <p className="text-slate-900 dark:text-slate-100">{traStandardLabel}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>TRA_REAL</p>
                    <p className="text-slate-900 dark:text-slate-100">{traRealLabel}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>DESVIO_%</p>
                    <p className={`${tpDeviationTone.valueClassName || 'text-slate-900 dark:text-slate-100'}`}>{formatPercent(tpDeviationPercent)}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>REPROC</p>
                    <p className={`${reprocessTone.valueClassName}`}>{execution.reprocessCount}</p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p>ERRO_%</p>
                    <p className="text-slate-900 dark:text-slate-100">{formatPercent(snapshotMetrics.errorStandardPercent)}</p>
                  </div>
                </div>
              </section>

              <section
                ref={historySectionRef}
                className={`rounded-[6px] border px-3 py-2.5 transition-colors ${
                  focusedPanel === 'history'
                    ? 'border-slate-400 bg-slate-100/70 dark:border-slate-500 dark:bg-slate-900'
                    : 'border-slate-200/80 bg-slate-50/40 dark:border-slate-800 dark:bg-slate-900/50'
                }`}
              >
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Historico da etapa</p>
                <div className="mt-2 max-h-[148px] overflow-y-auto pr-1">
                  {logsLoading ? (
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">Carregando...</p>
                  ) : recentLogs.length === 0 ? (
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">SEM_REGISTROS</p>
                  ) : (
                    recentLogs.map((log: any, index: number) => (
                      <div key={log.id ?? index} className="border-t border-slate-200/70 py-2 first:border-t-0 dark:border-slate-800">
                        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-800 dark:text-slate-100">{log.action}</p>
                        {log.justification ? <p className="mt-1 text-slate-500 dark:text-slate-400">{log.justification}</p> : null}
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">{new Date(log.created_at).toLocaleString('pt-BR')}</p>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <section
                ref={timelineSectionRef}
                className={`rounded-[6px] border px-3 py-2.5 transition-colors ${
                  focusedPanel === 'timeline'
                    ? 'border-slate-400 bg-slate-100/70 dark:border-slate-500 dark:bg-slate-900'
                    : 'border-slate-200/80 bg-slate-50/40 dark:border-slate-800 dark:bg-slate-900/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Eventos recentes</p>
                  {eventsLoading ? <Loader2 size={14} className="animate-spin text-slate-400" /> : null}
                </div>
                <div className="mt-2 max-h-[168px] overflow-y-auto pr-1">
                  {recentEvents.length === 0 ? (
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">SEM_EVENTOS</p>
                  ) : (
                    recentEvents.map((event, index) => (
                      <div key={event.id} className="border-t border-slate-200/70 py-2 first:border-t-0 dark:border-slate-800">
                        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-800 dark:text-slate-100">{event.description ?? event.eventType}</p>
                        <p className="mt-1 text-slate-500 dark:text-slate-400">
                          {event.stageName ?? 'SEM_ETAPA'}{event.performedByName ? ` | ${event.performedByName}` : ''}
                        </p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">{new Date(event.createdAt).toLocaleString('pt-BR')}</p>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <section
                ref={technicalSectionRef}
                className={`rounded-[6px] border px-3 py-2.5 transition-colors ${
                  focusedPanel === 'technical'
                    ? 'border-slate-400 bg-slate-100/70 dark:border-slate-500 dark:bg-slate-900'
                    : 'border-slate-200/80 bg-slate-50/40 dark:border-slate-800 dark:bg-slate-900/50'
                }`}
              >
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Governanca tecnica</p>
                <div className="mt-3 space-y-3 text-xs">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">OBSERVACOES</p>
                    <p className="mt-1 text-slate-700 dark:text-slate-300">{notes.trim() || task?.notes?.trim() || 'Nenhuma observacao registrada.'}</p>
                  </div>
                  <div className="border-t border-slate-200/70 pt-3 dark:border-slate-800">
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">WARNINGS</p>
                    <div className="mt-2 space-y-2">
                      {stageWarnings.length === 0 && !execution.isOverdue ? <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">SEM_WARNING_OPERACIONAL</p> : null}
                      {execution.isOverdue ? <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-rose-700 dark:text-rose-300">INSTANCIA_EM_ATRASO{execution.overdueMinutes ? ` | ${Math.round(execution.overdueMinutes)} MIN` : ''}</p> : null}
                      {tpDeviationPercent !== null && tpDeviationPercent > warningThresholdPercent ? <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300">DESVIO_TP {formatPercent(tpDeviationPercent)}</p> : null}
                    </div>
                  </div>
                  <div className="border-t border-slate-200/70 pt-3 dark:border-slate-800">
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">IDS</p>
                    <div className="mt-2 space-y-2">
                      {governanceRows.map((row) => (
                        <div key={row.label} className="rounded-[4px] border border-slate-200/80 px-2 py-2 dark:border-slate-800">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">{row.label}</span>
                            <button
                              type="button"
                              onClick={() => void handleCopyValue(row.label, row.value)}
                              className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                            >
                              copiar
                            </button>
                          </div>
                          <p className="mt-1 break-all font-mono text-[11px] text-slate-800 dark:text-slate-100">{row.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-slate-200/70 pt-3 dark:border-slate-800">
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">MODELO / VERSÃO</p>
                    <p className="mt-1 text-slate-700 dark:text-slate-300">{audit?.blueprintName ?? audit?.blueprintId ?? 'N/D'}</p>
                    <p className="font-mono text-[11px] text-slate-500 dark:text-slate-400">{audit?.versionSemver ?? 'N/D'}</p>
                  </div>
                  <div className="border-t border-slate-200/70 pt-3 dark:border-slate-800">
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">TEMPLATE / HASH</p>
                    <p className="mt-1 text-slate-700 dark:text-slate-300">{execution.templateName ?? audit?.templateName ?? 'N/D'}</p>
                    <p className="font-mono text-[11px] break-all text-slate-500 dark:text-slate-400">{audit?.snapshotHash ?? 'N/D'}</p>
                  </div>
                  <div className="border-t border-slate-200/70 pt-3 dark:border-slate-800">
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">TEMPOS</p>
                    <p className="mt-1 text-slate-700 dark:text-slate-300">Inicio {execution.startedAt ? new Date(execution.startedAt).toLocaleString('pt-BR') : 'N/D'}</p>
                    <p className="text-slate-500 dark:text-slate-400">Conclusao {execution.completedAt ? new Date(execution.completedAt).toLocaleString('pt-BR') : 'EM_ANDAMENTO'}</p>
                  </div>
                  <div className="border-t border-slate-200/70 pt-3 dark:border-slate-800">
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">DESVIO_ETAPA_ATUAL</p>
                    <p className="mt-1 font-mono text-slate-700 dark:text-slate-300">{formatPercent(stageDeviationPercent)}</p>
                  </div>
                </div>
              </section>
            </aside>
          </section>
        </div>
      </main>

      {showActionPanel && task ? (
        <div className="fixed inset-0 z-40 bg-slate-950/20 backdrop-blur-[1px]" onClick={() => setShowActionPanel(false)}>
          <div className="absolute inset-y-0 right-0 w-full max-w-[400px] overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <GestaoTaskDrawer
              task={task}
              isLoading={taskLoading}
              logs={logs}
              logsLoading={logsLoading}
              onClose={() => setShowActionPanel(false)}
              onReassign={gestaoMutations.reassignTask}
              onSkip={gestaoMutations.skipTask}
              onAdjustSLA={gestaoMutations.adjustSLA}
              isReassigning={gestaoMutations.isReassigning}
              isSkipping={gestaoMutations.isSkipping}
              isAdjustingSLA={gestaoMutations.isAdjustingSLA}
              currentUserId={userId ?? ''}
            />
          </div>
        </div>
      ) : null}

      <ToastContainer />
    </div>
  );
};
